import { reactive, computed, inject, watch } from 'vue';
import { cartItemToOrderRequest } from '@shared/types/cart';
import { MoneyUtils } from '../utils/MoneyUtils';
// Project-specific pricing states - each project gets its own isolated state
const projectPricingStates = new Map();
// Project-specific API call tracking to prevent race conditions
const projectApiCalls = new Map();
// Project-specific discount codes - preserves discount across automatic recalculations
const projectDiscountCodes = new Map();
// Project-specific composable instances - SINGLETON pattern to prevent multiple instances
const projectComposableInstances = new Map();
/**
 * Get or create project-specific pricing state
 * Ensures complete isolation between different restaurant projects
 */
export function getProjectPricingState(projectId) {
    if (!projectPricingStates.has(projectId)) {
        console.log(`🏗️ Creating new pricing state for project: ${projectId}`);
        projectPricingStates.set(projectId, reactive({
            pricingData: null,
            loading: false,
            error: null,
            lastCalculatedAt: null,
            requestStatus: null
        }));
    }
    return projectPricingStates.get(projectId);
}
/**
 * Get all project states (for debugging)
 */
export function getAllProjectStates() {
    return projectPricingStates;
}
/**
 * Clear specific project state (for cleanup)
 */
export function clearProjectState(projectId) {
    if (projectPricingStates.has(projectId)) {
        console.log(`🧹 Clearing pricing state for project: ${projectId}`);
        projectPricingStates.delete(projectId);
    }
    if (projectComposableInstances.has(projectId)) {
        console.log(`🧹 Clearing composable instance for project: ${projectId}`);
        projectComposableInstances.delete(projectId);
    }
}
/**
 * Debug function: Get all active instances
 */
export function getActiveInstances() {
    return projectComposableInstances;
}
/**
 * Debug function: Log all active instances
 */
export function debugActiveInstances() {
    console.log('🔍 Active pricing instances:');
    projectComposableInstances.forEach((instance, projectId) => {
        console.log(`  📋 ${projectId}: ${instance._instanceId}`, {
            loading: instance._state.loading,
            hasPricingData: !!instance._state.pricingData,
            lastCalculated: instance._state.lastCalculatedAt
        });
    });
}
// Note: globalPricingState has been removed in favor of project-specific state management
// Use useOrderPricing(projectId) to get project-specific state
/**
 * Enhanced Composable for Order Pricing Management
 * Shared across all restaurant projects with project-specific state isolation
 * Handles automatic pricing calculations with tax, service fees, and discounts
 *
 * SINGLETON PATTERN: Each project gets exactly ONE shared instance across all components
 *
 * @param projectId - Optional project identifier for state isolation (defaults to injected apiClient project)
 */
export function useOrderPricing(projectId) {
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('RestaurantApiClient not provided. Make sure to provide it in your app.');
    }
    // Determine project ID - try to get from window context or use provided projectId
    const resolvedProjectId = projectId ||
        (typeof window !== 'undefined' && window.projectConfig?.projectIdentifier) ||
        'default';
    // SINGLETON: Return existing instance if already created for this project
    if (projectComposableInstances.has(resolvedProjectId)) {
        const existingInstance = projectComposableInstances.get(resolvedProjectId);
        console.log(`♻️ [${resolvedProjectId}] Reusing existing composable instance:`, existingInstance._instanceId);
        return existingInstance;
    }
    console.log(`🆕 [${resolvedProjectId}] Creating NEW composable instance (SINGLETON)`);
    // Get project-specific state
    const projectPricingState = getProjectPricingState(resolvedProjectId);
    // Generate unique instance ID for debugging - but this should be SINGLETON per project
    const instanceId = `SINGLETON-${Math.random().toString(36).substr(2, 6)}`;
    /**
     * Enhanced auto-initialize pricing calculation if cart has items but no pricing data exists
     * Now checks for delivery type and ensures proper calculation on page reload
     */
    const autoInitialize = async () => {
        // Check if we're in a browser environment and have a cart store
        if (typeof window !== 'undefined' && !projectPricingState.pricingData && !projectPricingState.loading) {
            try {
                // Try to get cart items and delivery type from global store (if available)
                const cartStore = window.__CART_STORE__;
                if (cartStore?.items?.length > 0) {
                    const deliveryType = cartStore.deliveryType || 'pickup';
                    // ✅ CRITICAL: Format deliveryAddress as string for delivery orders
                    let deliveryAddr = undefined;
                    if (deliveryType === 'delivery' && cartStore.deliveryAddress) {
                        const { street, city, postalCode } = cartStore.deliveryAddress;
                        if (street?.trim() && city?.trim() && postalCode?.trim()) {
                            deliveryAddr = `${street.trim()}, ${postalCode.trim()} ${city.trim()}`;
                        }
                    }
                    await calculateOrderPricing(cartStore.items, deliveryType, deliveryAddr);
                }
            }
            catch (error) {
                // Silently fail - this is just auto-initialization
                console.warn(`Auto-initialization failed for project ${resolvedProjectId}:`, error);
            }
        }
    };
    /**
     * Calculate order pricing with comprehensive breakdown
     */
    const calculateOrderPricing = async (items, deliveryType, deliveryAddress, discountCode // ✅ NEW: Discount code parameter - can be string or array
    ) => {
        // ✅ DISCOUNT: Store discount code for this project (persists across automatic recalculations)
        if (discountCode !== undefined) {
            projectDiscountCodes.set(resolvedProjectId, discountCode);
            console.log(`� [${resolvedProjectId}] Stored discount code:`, discountCode);
        }
        // ✅ DISCOUNT: Use stored discount code if none provided (for automatic recalculations)
        const activeDiscountCode = discountCode ?? projectDiscountCodes.get(resolvedProjectId);
        if (activeDiscountCode && !discountCode) {
            console.log(`♻️ [${resolvedProjectId}] Using stored discount code:`, activeDiscountCode);
        }
        // �🚨 ENHANCED DEBUG: Erweiterte Debug-Logs für calculateOrderPricing
        console.log(`🚀🚀🚀 [${resolvedProjectId}:${instanceId}] calculateOrderPricing ENHANCED DEBUG START`, {
            timestamp: new Date().toISOString(),
            '--- CALL PARAMETERS ---': true,
            itemsCount: items?.length || 0,
            deliveryType,
            discountCode: activeDiscountCode, // ✅ Show active discount code
            itemsPreview: items?.map(item => ({ id: item.id, name: item.menuItem?.name || item.name, price: item.menuItem?.price || item.price, quantity: item.quantity })) || [],
            '--- CURRENT STATE ---': true,
            currentLoadingState: projectPricingState.loading,
            currentPricingData: !!projectPricingState.pricingData,
            currentError: projectPricingState.error,
            lastCalculatedAt: projectPricingState.lastCalculatedAt,
            '--- CART STORE CONTEXT ---': true,
            cartStoreDeliveryType: (typeof window !== 'undefined' && window.__CART_STORE__?.deliveryType) || 'unknown',
            cartStoreItemCount: (typeof window !== 'undefined' && window.__CART_STORE__?.items?.length) || 0,
            '--- PENDING CALLS ---': true,
            hasPendingCall: !!projectApiCalls.get(resolvedProjectId)?.controller,
            pendingCallTimestamp: projectApiCalls.get(resolvedProjectId)?.timestamp,
            '--- INSTANCE INFO ---': true,
            projectId: resolvedProjectId,
            instanceId,
            singletonCheck: projectComposableInstances.has(resolvedProjectId)
        });
        if (!items || items.length === 0) {
            console.log(`🚀🛒 [${resolvedProjectId}:${instanceId}] No items - clearing pricing data`, {
                reason: 'empty_cart',
                action: 'clearing_state'
            });
            projectPricingState.pricingData = null;
            projectPricingState.error = null;
            return null;
        }
        // Cancel any pending API calls for this project
        const currentCall = projectApiCalls.get(resolvedProjectId);
        if (currentCall?.controller) {
            console.log(`🛑 [${resolvedProjectId}:${instanceId}] Canceling previous API call`);
            currentCall.controller.abort();
        }
        // Create new AbortController for this call
        const abortController = new AbortController();
        projectApiCalls.set(resolvedProjectId, {
            controller: abortController,
            timestamp: Date.now()
        });
        // 🚨 ENHANCED DEBUG: Loading State Management
        console.log(`🚀⏳ [${resolvedProjectId}:${instanceId}] Setting loading = true - ENHANCED DEBUG`, {
            previousLoadingState: projectPricingState.loading,
            willSetLoadingTo: true,
            timestamp: new Date().toISOString(),
            reason: 'starting_api_calculation',
            deliveryType,
            itemCount: items.length
        });
        projectPricingState.loading = true;
        projectPricingState.error = null;
        projectPricingState.requestStatus = null; // Reset status before new request
        // Emit global loading event with enhanced debugging
        console.log(`🚀📡 [${resolvedProjectId}:${instanceId}] Emitting orderPricingLoading event (LOADING START)`, {
            eventDetail: {
                loading: true,
                projectId: resolvedProjectId,
                instance: instanceId,
                source: 'calculateOrderPricing',
                deliveryType,
                itemCount: items.length
            }
        });
        window.dispatchEvent(new CustomEvent('orderPricingLoading', {
            detail: {
                loading: true,
                projectId: resolvedProjectId,
                instance: instanceId,
                source: 'calculateOrderPricing',
                deliveryType,
                itemCount: items.length
            }
        }));
        try {
            // 🐛 DEBUG: Log cart items structure to find the correct ID
            console.log(`🔍 [${resolvedProjectId}:${instanceId}] DEBUGGING CART ITEMS STRUCTURE:`, {
                itemsCount: items.length,
                firstItem: items[0] ? {
                    id: items[0].id,
                    menuItemId: items[0].menuItem?.id,
                    menuItemName: items[0].menuItem?.name,
                    quantity: items[0].quantity,
                    selectedSize: items[0].selectedSize,
                    fullStructure: items[0]
                } : 'no items'
            });
            const pricingRequest = {
                items: items.map((item, index) => {
                    const orderItem = cartItemToOrderRequest(item);
                    console.log(`🔍 [${resolvedProjectId}:${instanceId}] Item ${index + 1} transformation:`, {
                        itemType: orderItem.itemType || 'product',
                        id: orderItem.id,
                        menuId: orderItem.menuId
                    });
                    return orderItem;
                }),
                deliveryType: deliveryType || 'pickup',
                // ✅ FIX: Format delivery address if it's an object
                deliveryAddress: deliveryAddress
                    ? (typeof deliveryAddress === 'string'
                        ? deliveryAddress
                        : `${deliveryAddress.street}, ${deliveryAddress.postalCode} ${deliveryAddress.city}`)
                    : undefined,
                // ✅ NEW: Include discount code(s) in request - always send as array
                discountCodes: activeDiscountCode
                    ? (Array.isArray(activeDiscountCode) ? activeDiscountCode : [activeDiscountCode])
                    : undefined
            };
            console.log(`📡 [${resolvedProjectId}:${instanceId}] Making API call with request:`, pricingRequest);
            const apiCallStart = Date.now();
            const response = await apiClient.calculateOrderPricing(pricingRequest);
            // Check if this call was aborted
            if (abortController.signal.aborted) {
                console.log(`🛑 [${resolvedProjectId}:${instanceId}] API call was aborted - ignoring response`);
                return null;
            }
            const apiCallEnd = Date.now();
            console.log(`📡 [${resolvedProjectId}:${instanceId}] API call completed in ${apiCallEnd - apiCallStart}ms`);
            console.log(`💰 [${resolvedProjectId}] API Response received:`, {
                status: response.status,
                code: response.code,
                message: response.message,
                hasData: !!response.data,
                dataKeys: response.data ? Object.keys(response.data) : [],
                fullResponse: response
            });
            if (response.status === 'success' && response.data) {
                console.log(`✅ [${resolvedProjectId}] Success response - updating pricing state`);
                projectPricingState.pricingData = response;
                projectPricingState.requestStatus = 200; // Success
                projectPricingState.lastCalculatedAt = new Date();
                // Force reactivity update by touching the reactive state
                projectPricingState.lastCalculatedAt = new Date(Date.now() + 1);
                console.log(`✅ [${resolvedProjectId}] Pricing state updated successfully - status: 200`);
                // 🕐 EMIT OPERATING HOURS EVENT: Check for operating hours in successful response
                if (response.data?.delivery?.availableTimeSlots) {
                    const slots = response.data.delivery.availableTimeSlots;
                    console.log(`🕐 [${resolvedProjectId}] Found operating hours in API response:`, slots);
                    if (slots?.enabled && slots?.hours?.length) {
                        const operatingHours = slots.hours.map((slot) => ({
                            open: slot.start,
                            close: slot.end
                        }));
                        // Emit operating hours event using the same pattern as other events
                        console.log(`🕐📡 [${resolvedProjectId}] Emitting operatingHoursAvailable event:`, operatingHours);
                        window.dispatchEvent(new CustomEvent('operatingHoursAvailable', {
                            detail: {
                                operatingHours,
                                projectId: resolvedProjectId,
                                source: 'useOrderPricing_api_success',
                                timestamp: new Date().toISOString()
                            }
                        }));
                    }
                }
                return response;
            }
            else if (response.code === 'RESTAURANT_CLOSED') {
                // Handle restaurant closed - now with status: 'success' but restaurant closed
                const errorMsg = response.message || 'Restaurant ist geschlossen';
                projectPricingState.error = errorMsg;
                projectPricingState.pricingData = response; // Store response for UI to access restaurant status
                projectPricingState.requestStatus = 200; // Restaurant closed is valid response (not server error)
                projectPricingState.lastCalculatedAt = new Date();
                console.warn(`🚫 [${resolvedProjectId}] Restaurant closed (status: 200):`, errorMsg);
                // Emit restaurant closed event for global handling
                window.dispatchEvent(new CustomEvent('restaurantClosed', {
                    detail: {
                        projectId: resolvedProjectId,
                        message: errorMsg,
                        isOpen: response.isOpen
                    }
                }));
                return response;
            }
            else if (response.status === 'error' && response.code === 'MINIMUM_ORDER_NOT_REACHED') {
                // Handle minimum order value not reached - this is a normal validation, not an error
                console.log(`📊 [${resolvedProjectId}] Minimum order not reached - processing response:`, {
                    response,
                    hasData: !!response.data,
                    data: response.data,
                    message: response.message
                });
                projectPricingState.error = null; // Clear error since this is expected validation
                projectPricingState.pricingData = response; // Store error response for UI to access minimum order details
                projectPricingState.requestStatus = 200; // Minimum order validation is valid response
                projectPricingState.lastCalculatedAt = new Date();
                console.log(`📊 [${resolvedProjectId}] Minimum order state updated:`, {
                    pricingDataSet: !!projectPricingState.pricingData,
                    pricingDataCode: projectPricingState.pricingData?.code,
                    pricingDataStatus: projectPricingState.pricingData?.status,
                    hasResponseData: !!projectPricingState.pricingData?.data
                });
                // Force reactivity update by touching the reactive state
                projectPricingState.lastCalculatedAt = new Date(Date.now() + 1);
                console.log(`🔄 [${resolvedProjectId}] Forced reactivity update - testing computed properties`);
                // Manually trigger computed properties to test reactivity
                setTimeout(() => {
                    console.log(`🧪 [${resolvedProjectId}] Testing reactivity - orderStatusMessage:`, projectPricingState.pricingData);
                }, 0);
                // Emit minimum order event for global handling
                window.dispatchEvent(new CustomEvent('minimumOrderNotReached', {
                    detail: {
                        projectId: resolvedProjectId,
                        message: response.message,
                        data: response.data
                    }
                }));
                console.log(`📊 [${resolvedProjectId}] Minimum order event dispatched`);
                return response;
            }
            else {
                const errorMsg = response.message || 'Failed to calculate order pricing';
                projectPricingState.error = errorMsg;
                projectPricingState.requestStatus = response.status === 'error' ? 400 : 500; // Set appropriate error status
                console.error(`❌ [${resolvedProjectId}] Pricing calculation failed (status: ${projectPricingState.requestStatus}):`, errorMsg);
                return null;
            }
        }
        catch (error) {
            const errorMsg = error.message || 'Unknown error during pricing calculation';
            projectPricingState.error = errorMsg;
            // Extract status code from error if available
            if (error.response?.status) {
                projectPricingState.requestStatus = error.response.status;
                console.error(`❌ [${resolvedProjectId}] API error (HTTP ${error.response.status}):`, error);
            }
            else if (error.message?.includes('ERR_CONNECTION_REFUSED') || error.message?.includes('NetworkError') || error.code === 'ERR_NETWORK') {
                projectPricingState.requestStatus = 0; // Network error
                console.error(`❌ [${resolvedProjectId}] Network error - setting requestStatus to 0:`, error);
            }
            else {
                projectPricingState.requestStatus = 500; // Generic server error
                console.error(`❌ [${resolvedProjectId}] Unknown error:`, error);
            }
            // Don't return null - this breaks the flow. Instead rely on requestStatus
            console.warn(`⚠️ [${resolvedProjectId}] Network error handled, requestStatus set to: ${projectPricingState.requestStatus}`);
            // Use fallback for now, but the requestStatus will trigger the error screen
            return calculateFallbackPricing(items);
        }
        finally {
            // Only reset loading if this is the current call
            const currentCall = projectApiCalls.get(resolvedProjectId);
            if (currentCall?.controller === abortController) {
                // 🚨 ENHANCED DEBUG: Loading State Reset
                console.log(`🏁🏁 [${resolvedProjectId}:${instanceId}] calculateOrderPricing FINALLY - ENHANCED DEBUG`, {
                    action: 'setting_loading_false',
                    reason: 'current_call_completed',
                    previousLoadingState: projectPricingState.loading,
                    willSetLoadingTo: false,
                    timestamp: new Date().toISOString(),
                    finalPricingData: !!projectPricingState.pricingData,
                    finalPricingStatus: projectPricingState.pricingData?.status,
                    finalPricingCode: projectPricingState.pricingData?.code,
                    deliveryType
                });
                projectPricingState.loading = false;
                // Clear the current call tracker
                projectApiCalls.delete(resolvedProjectId);
                // Emit global loading event with enhanced debugging
                console.log(`🏁📡 [${resolvedProjectId}:${instanceId}] Emitting orderPricingLoading event (LOADING END)`, {
                    eventDetail: {
                        loading: false,
                        projectId: resolvedProjectId,
                        instance: instanceId,
                        source: 'calculateOrderPricing',
                        deliveryType,
                        finalStatus: projectPricingState.pricingData?.status,
                        finalCode: projectPricingState.pricingData?.code
                    }
                });
                window.dispatchEvent(new CustomEvent('orderPricingLoading', {
                    detail: {
                        loading: false,
                        projectId: resolvedProjectId,
                        instance: instanceId,
                        source: 'calculateOrderPricing',
                        deliveryType,
                        finalStatus: projectPricingState.pricingData?.status,
                        finalCode: projectPricingState.pricingData?.code
                    }
                }));
                console.log(`✅✅ [${resolvedProjectId}:${instanceId}] calculateOrderPricing COMPLETE - loading state should be false`, {
                    loadingStateConfirm: projectPricingState.loading,
                    hasFinalData: !!projectPricingState.pricingData,
                    isDataValid: projectPricingState.pricingData?.status === 'success',
                    reason: 'api_call_completed_successfully'
                });
            }
            else {
                console.log(`⏭️⏭️ [${resolvedProjectId}:${instanceId}] calculateOrderPricing FINALLY - ENHANCED DEBUG`, {
                    action: 'skipping_loading_reset',
                    reason: 'not_current_call',
                    currentCallController: !!currentCall?.controller,
                    thisCallController: !!abortController,
                    controllersMatch: currentCall?.controller === abortController,
                    timestamp: new Date().toISOString()
                });
            }
        }
    };
    /**
     * Fallback pricing calculation when API is unavailable
     */
    const calculateFallbackPricing = (items) => {
        console.log(`⚠️ [${resolvedProjectId}] Using fallback pricing calculation`);
        const subtotal = items.reduce((total, item) => {
            return total + item.totalPrice;
        }, 0);
        // ⚠️ WICHTIG: Produktpreise sind bereits BRUTTO (inkl. MwSt). Ohne API-Antwort
        // kennen wir weder die echte Service-Gebühr noch die Lieferkosten. Wir dürfen
        // daher KEINE Gebühren erfinden und die MwSt NICHT erneut aufschlagen –
        // sonst erscheinen Phantom-Beträge (z. B. eine 2%-Service-Gebühr und ein zu
        // hoher Gesamtbetrag), obwohl das Projekt diese gar nicht konfiguriert hat.
        const taxRate = 0.19; // 19% MwSt – nur zur (inklusiven) Anzeige
        const serviceFee = 0;
        const deliveryFee = 0;
        // MwSt aus dem Bruttobetrag herausrechnen (nicht aufschlagen).
        const taxAmount = subtotal - (subtotal / (1 + taxRate));
        const total = subtotal + deliveryFee + serviceFee;
        const fallbackPricing = {
            status: 'success',
            message: 'Fallback pricing calculation',
            isOpen: true,
            code: 'FALLBACK_PRICING',
            project: resolvedProjectId,
            timestamp: new Date().toISOString(),
            data: {
                items: items.map(item => ({
                    id: item.id,
                    name: item.menuItem.name,
                    price: item.menuItem.price,
                    quantity: item.quantity,
                    selectedSizeId: item.selectedSize?.id || undefined
                })),
                // ✅ OPTIMIZED: Include both legacy and optimized structures for compatibility
                pricing: {
                    subtotal,
                    deliveryFee,
                    serviceFee,
                    taxAmount,
                    total,
                    currency: 'EUR',
                    // Add cent-based values for precision
                    subtotalCent: MoneyUtils.euroToCent(subtotal),
                    totalCent: MoneyUtils.euroToCent(total),
                    deliveryFeeCent: MoneyUtils.euroToCent(deliveryFee),
                    serviceFeeCent: MoneyUtils.euroToCent(serviceFee)
                },
                // Add optimized structure fields
                delivery: {
                    type: 'pickup',
                    feeCent: 0,
                    reason: 'pickup_free',
                    isEligibleForFree: true
                },
                validation: {
                    minimumOrder: {
                        isReached: true,
                        required: 0,
                        current: subtotal,
                        missing: 0,
                        message: null
                    }
                },
                tax: {
                    totalCent: MoneyUtils.euroToCent(taxAmount),
                    breakdown: {
                        '19': { rate: 0.19, net: subtotal - taxAmount, tax: taxAmount }
                    },
                    serviceType: 'takeaway'
                },
                deliveryInfo: {
                    type: 'pickup',
                    freeDeliveryThreshold: 25,
                    estimatedTime: '30-45 min',
                    pickupTime: { min: 30, max: 45 },
                    deliveryTime: { min: 45, max: 60 },
                    estimatedPickupTime: { min: '30', max: '45', display: '30-45 min' },
                    estimatedDeliveryTime: { min: '45', max: '60', display: '45-60 min' },
                    timezone: 'Europe/Berlin'
                }
            }
        };
        projectPricingState.pricingData = fallbackPricing;
        projectPricingState.lastCalculatedAt = new Date();
        projectPricingState.error = 'Using fallback pricing - API unavailable';
        return fallbackPricing;
    };
    /**
     * Clear pricing data
     */
    const clearPricing = () => {
        projectPricingState.pricingData = null;
        projectPricingState.error = null;
        projectPricingState.lastCalculatedAt = null;
        projectPricingState.requestStatus = null;
        console.log(`🧹 [${resolvedProjectId}] Order pricing cleared (including requestStatus)`);
    };
    /**
     * Clear discount code (called when user removes discount)
     */
    const clearDiscountCode = () => {
        projectDiscountCodes.delete(resolvedProjectId);
        console.log(`🧹 [${resolvedProjectId}] Discount code cleared`);
    };
    /**
     * ✅ OPTIMIZED: Format currency values using MoneyUtils for consistent cent-based precision
     * Always uses MoneyUtils.formatCurrency() for consistent formatting across the app
     */
    const formatCurrency = (amount, centAmount) => {
        // Prefer cent-based formatting when available for precision
        if (centAmount !== undefined && centAmount !== null) {
            return MoneyUtils.formatCurrency(centAmount);
        }
        // Convert euro amount to cents and use MoneyUtils for consistency
        const centValue = MoneyUtils.euroToCent(amount);
        return MoneyUtils.formatCurrency(centValue);
    };
    // ✅ OPTIMIZED: Computed Properties using cent-based precision from optimized API
    const subtotal = computed(() => {
        const pricingData = projectPricingState.pricingData;
        // Use optimized API structure with cent-based precision
        if (pricingData?.status === 'success' && pricingData.data) {
            const data = pricingData.data;
            // Use cent value with MoneyUtils for precision, fallback to euro value
            const centValue = data.pricing?.subtotalCent;
            if (centValue !== undefined) {
                return MoneyUtils.centToEuro(centValue);
            }
            return data.pricing?.subtotal || 0;
        }
        return 0;
    });
    const tax = computed(() => {
        const pricingData = projectPricingState.pricingData;
        if (pricingData?.status === 'success' && pricingData.data) {
            const data = pricingData.data;
            // Use optimized tax structure
            const centValue = data.tax?.totalCent;
            if (centValue !== undefined) {
                return MoneyUtils.centToEuro(centValue);
            }
            return data.pricing?.taxAmount || 0;
        }
        return 0;
    });
    const serviceFee = computed(() => {
        const pricingData = projectPricingState.pricingData;
        if (pricingData?.status === 'success' && pricingData.data) {
            const data = pricingData.data;
            // Service fee is included in tax breakdown - calculate from cent values
            const centValue = data.pricing?.serviceFeeCent;
            if (centValue !== undefined) {
                return MoneyUtils.centToEuro(centValue);
            }
            return data.pricing?.serviceFee || 0;
        }
        return 0;
    });
    const deliveryFee = computed(() => {
        const pricingData = projectPricingState.pricingData;
        console.log('🔍 [useOrderPricing] deliveryFee computed:', {
            feeCent: pricingData?.data?.delivery?.feeCent
        });
        if (pricingData?.status === 'success' && pricingData.data) {
            const data = pricingData.data;
            // Use optimized delivery structure
            const centValue = data.delivery?.feeCent;
            if (centValue !== undefined) {
                const euroValue = MoneyUtils.centToEuro(centValue);
                console.log('🔍 [useOrderPricing] deliveryFee centToEuro:', centValue, '→', euroValue);
                return euroValue;
            }
            return data.pricing?.deliveryFee || 0;
        }
        return 0;
    });
    const total = computed(() => {
        // Force reactivity by accessing the shared state directly from the Map
        const sharedState = projectPricingStates.get(resolvedProjectId);
        const pricingData = sharedState?.pricingData;
        sharedState?.lastCalculatedAt; // Force reactivity
        console.log('🔍 [useOrderPricing] total computed for project:', resolvedProjectId, {
            hasPricingData: !!pricingData,
            status: pricingData?.status,
            dataExists: !!pricingData?.data,
            pricingExists: !!pricingData?.data?.pricing,
            totalCent: pricingData?.data?.pricing?.totalCent
        });
        // ✅ OPTIMIZED: Use cent-based precision from optimized API
        if (pricingData?.status === 'success' && pricingData.data) {
            const responseData = pricingData.data;
            // Use cent value with MoneyUtils for precision
            const totalCent = responseData?.pricing?.totalCent;
            console.log('🔍 [useOrderPricing] Found totalCent:', totalCent);
            if (totalCent !== undefined) {
                try {
                    const euroValue = MoneyUtils.centToEuro(totalCent);
                    console.log('🔍 [useOrderPricing] Converted to Euro:', totalCent, '→', euroValue);
                    return euroValue;
                }
                catch (error) {
                    console.error('❌ [useOrderPricing] centToEuro conversion failed:', error, 'input:', totalCent);
                    return 0;
                }
            }
            // Fallback to euro-based values (for backward compatibility)
            const totalValue = responseData?.pricing?.total ||
                responseData?.taxCalculation?.totalGross || 0;
            return totalValue;
        }
        return 0;
    });
    // Force reactivity with explicit watch
    watch(() => projectPricingState.pricingData, (newData, oldData) => {
        if (newData !== oldData && newData?.data) {
            // Force computed to recalculate by accessing it
            const currentTotal = total.value;
            // Emit custom event to notify all components (only once per change)
            if (typeof window !== 'undefined') {
                const currentSubtotal = subtotal.value;
                const currentDeliveryFee = deliveryFee.value;
                const currentServiceFee = serviceFee.value;
                window.dispatchEvent(new CustomEvent('orderPricingChanged', {
                    detail: {
                        projectId: resolvedProjectId,
                        total: currentTotal,
                        subtotal: currentSubtotal,
                        deliveryFee: currentDeliveryFee,
                        serviceFee: currentServiceFee,
                        pricingData: newData,
                        instanceId: instanceId // Add instance ID for debugging
                    }
                }));
                console.log(`📡 [${resolvedProjectId}:${instanceId}] Emitted orderPricingChanged event with total: ${currentTotal}, subtotal: ${currentSubtotal}, deliveryFee: ${currentDeliveryFee}`);
            }
        }
    }, { deep: true });
    // ✅ DISABLED: Automatic recalculation on delivery type change
    // Previously, this watcher automatically triggered pricing calculation when delivery type changed,
    // but it couldn't reliably access the delivery address, causing duplicate requests without address.
    // 
    // SOLUTION: Components that change delivery type MUST explicitly call calculateOrderPricing()
    // with the proper deliveryAddress parameter. This ensures:
    // 1. No duplicate requests
    // 2. Delivery address is always included for delivery orders
    // 3. Zone-specific minimum order validation works correctly
    //
    // Example usage in components:
    // ```typescript
    // watch(() => cartStore.deliveryType, async (newType) => {
    //   if (newType === 'delivery' && deliveryAddress.value) {
    //     await orderPricing.calculateOrderPricing(
    //       cartStore.items, 
    //       newType, 
    //       deliveryAddress.value // ✅ Always pass address for delivery
    //     )
    //   } else if (newType === 'pickup') {
    //     await orderPricing.calculateOrderPricing(cartStore.items, newType)
    //   }
    // })
    // ```
    console.log(`ℹ️ [${resolvedProjectId}:${instanceId}] Automatic deliveryType watcher DISABLED - components must explicitly call calculateOrderPricing() with deliveryAddress`);
    const totalAmount = computed(() => total.value); // Alias for backward compatibility
    const currency = computed(() => {
        const pricingData = projectPricingState.pricingData;
        if (pricingData?.status === 'success' && pricingData.data?.pricing) {
            return pricingData.data.pricing.currency || 'EUR';
        }
        return 'EUR';
    });
    // ✅ OPTIMIZED: Tax breakdown from optimized API response structure
    const taxBreakdown = computed(() => {
        // Force reactivity by watching multiple state properties
        const pricingData = projectPricingState.pricingData;
        const lastCalc = projectPricingState.lastCalculatedAt;
        if (pricingData?.status !== 'success' || !pricingData?.data) {
            console.log(`🧾 [${resolvedProjectId}] taxBreakdown computed: no pricing data available`);
            return {};
        }
        const data = pricingData.data;
        // Use optimized tax structure
        const breakdown = data.tax?.breakdown || data.pricing?.taxBreakdown || {};
        console.log(`🧾 [${resolvedProjectId}] taxBreakdown computed (OPTIMIZED):`, {
            hasPricingData: !!pricingData,
            lastCalculatedAt: lastCalc,
            breakdown,
            keys: Object.keys(breakdown),
            optimizedTaxBreakdown: data.tax?.breakdown,
            fallbackTaxBreakdown: data.pricing?.taxBreakdown
        });
        // Ensure we return a reactive copy
        return { ...breakdown };
    });
    const taxCalculation = computed(() => {
        // Force reactivity by watching multiple state properties
        const pricingData = projectPricingState.pricingData;
        const lastCalc = projectPricingState.lastCalculatedAt;
        if (pricingData?.status !== 'success' || !pricingData?.data) {
            console.log(`🧾 [${resolvedProjectId}] taxCalculation computed: no pricing data available`);
            return null;
        }
        const responseData = pricingData.data;
        // ✅ OPTIMIZED: Use optimized API response structure with cent-based precision
        const pricing = responseData.pricing;
        const tax = responseData.tax;
        // Convert cent values to euros for display (PURE CENT - no fallbacks)
        const totalNet = pricing?.subtotalCent ? MoneyUtils.centToEuro(pricing.subtotalCent) : 0;
        const totalTax = tax?.totalCent ? MoneyUtils.centToEuro(tax.totalCent) : 0;
        const totalGross = pricing?.totalCent ? MoneyUtils.centToEuro(pricing.totalCent) : 0;
        const result = {
            totalNet,
            totalTax,
            totalGross,
            serviceType: tax?.serviceType || 'takeaway'
        };
        console.log(`🧾 [${resolvedProjectId}] taxCalculation computed (OPTIMIZED):`, {
            hasPricing: !!pricing,
            lastCalculatedAt: lastCalc,
            result,
            optimizedTaxData: tax,
            pricingData: pricing,
            centValues: {
                subtotalCent: pricing?.subtotalCent,
                taxCent: tax?.totalCent,
                totalCent: pricing?.totalCent
            }
        });
        return result;
    });
    const hasPricing = computed(() => projectPricingState.pricingData !== null);
    const isLoading = computed(() => {
        const loadingState = projectPricingState.loading;
        console.log(`⚡ [${resolvedProjectId}:${instanceId}] isLoading computed:`, {
            loadingState,
            timestamp: new Date().toISOString()
        });
        return loadingState;
    });
    const hasError = computed(() => !!projectPricingState.error);
    const error = computed(() => projectPricingState.error);
    // Restaurant status from pricing response
    const isRestaurantClosed = computed(() => {
        return projectPricingState.pricingData?.isOpen ? false : true;
    });
    const restaurantClosedMessage = computed(() => {
        if (isRestaurantClosed.value) {
            return 'Das Restaurant ist derzeit geschlossen.';
        }
        return null;
    });
    // ✅ OPTIMIZED: Minimum order status from optimized API response structure
    const minimumOrderInfo = computed(() => {
        const pricingData = projectPricingState.pricingData;
        // 🚨 ENHANCED DEBUG: Erweiterte Minimum Order Info Logs
        console.log(`📊📊📊 [${resolvedProjectId}] minimumOrderInfo ENHANCED DEBUG:`, {
            timestamp: new Date().toISOString(),
            computedTrigger: 'minimumOrderInfo_called',
            '--- PRICING DATA STATE ---': true,
            hasPricingData: !!pricingData,
            status: pricingData?.status,
            code: pricingData?.code,
            message: pricingData?.message,
            hasData: !!pricingData?.data,
            '--- SERVER DATA ANALYSIS ---': true,
            serverDataKeys: pricingData?.data ? Object.keys(pricingData.data) : [],
            hasServerValidation: !!pricingData?.data?.validation,
            serverValidationKeys: pricingData?.data?.validation ? Object.keys(pricingData.data.validation) : [],
            serverMinimumOrder: pricingData?.data?.validation?.minimumOrder,
            '--- PRICING DATA DETAILS ---': true,
            hasPricingStructure: !!pricingData?.data?.pricing,
            pricingKeys: pricingData?.data?.pricing ? Object.keys(pricingData.data.pricing) : [],
            subtotalCent: pricingData?.data?.pricing?.subtotalCent,
            subtotalEuro: pricingData?.data?.pricing?.subtotal,
            '--- DELIVERY INFO ---': true,
            hasDeliveryInfo: !!pricingData?.data?.delivery,
            deliveryType: pricingData?.data?.delivery?.type,
            deliveryFeeCent: pricingData?.data?.delivery?.feeCent,
            '--- ERROR SPECIFIC DATA ---': true,
            isMinimumOrderError: pricingData?.status === 'error' && pricingData?.code === 'MINIMUM_ORDER_NOT_REACHED',
            errorSpecificData: pricingData?.status === 'error' ? pricingData.data : null
        });
        if (!pricingData) {
            console.log(`📊❌ [${resolvedProjectId}] No pricing data - returning null`);
            return null;
        }
        // Check if minimum order validation failed
        if (pricingData.status === 'error' && pricingData.code === 'MINIMUM_ORDER_NOT_REACHED' && pricingData.data) {
            const data = pricingData.data;
            const info = {
                isReached: false,
                required: data.minimumRequired || 0,
                current: data.currentAmount || 0,
                missing: data.missingAmount || 0,
                message: pricingData.message || 'Mindestbestellwert nicht erreicht'
            };
            console.log(`📊 [${resolvedProjectId}] Minimum order validation failed:`, info);
            return info;
        }
        // ✅ OPTIMIZED: Check successful pricing data using optimized structure
        if (pricingData.status === 'success' && pricingData.data) {
            const data = pricingData.data;
            // Use cent-based values for precision, fallback to euro values
            const subtotal = data.pricing?.subtotalCent ?
                MoneyUtils.centToEuro(data.pricing.subtotalCent) :
                (data.pricing?.subtotal || 0);
            const deliveryFee = data.delivery?.feeCent ?
                MoneyUtils.centToEuro(data.delivery.feeCent) :
                (data.pricing?.deliveryFee || 0);
            // Check validation results from server
            if (data.validation?.minimumOrder) {
                const validation = data.validation.minimumOrder;
                // ✅ FIX: Convert cent values to euro for display
                const requiredEuro = validation.requiredCent
                    ? MoneyUtils.centToEuro(validation.requiredCent)
                    : (validation.required || 0);
                const currentEuro = validation.currentCent
                    ? MoneyUtils.centToEuro(validation.currentCent)
                    : (validation.current || subtotal);
                const missingEuro = Math.max(0, requiredEuro - currentEuro);
                const info = {
                    isReached: validation.met ?? validation.isReached ?? false,
                    required: requiredEuro,
                    current: currentEuro,
                    missing: missingEuro,
                    message: validation.message || null
                };
                console.log(`📊 [${resolvedProjectId}] Using server validation results (converted from cent):`, info);
                return info;
            }
            // Fallback logic if no validation info (backward compatibility)
            if (deliveryFee === 0) {
                const info = {
                    isReached: true, // Pickup always passes minimum order
                    required: 0,
                    current: subtotal,
                    missing: 0,
                    message: null
                };
                console.log(`📊 [${resolvedProjectId}] Pickup - no minimum order requirement:`, info);
                return info;
            }
            // For delivery, assume requirement met if successful response
            const info = {
                isReached: true,
                required: 0,
                current: subtotal,
                missing: 0,
                message: null
            };
            console.log(`📊 [${resolvedProjectId}] Delivery with success - minimum order requirement met:`, info);
            return info;
        }
        console.log(`📊 [${resolvedProjectId}] No minimum order info available - returning null`);
        return null;
    });
    const isMinimumOrderReached = computed(() => {
        const info = minimumOrderInfo.value;
        const pricingData = projectPricingState.pricingData;
        const isLoading = projectPricingState.loading;
        // ✅ CRITICAL: Determine if this is a pickup or delivery order
        const data = pricingData?.data;
        const deliveryType = data?.delivery?.type || 'pickup';
        const isPickup = deliveryType === 'pickup';
        const deliveryFee = data?.delivery?.feeCent ?
            MoneyUtils.centToEuro(data.delivery.feeCent) : 0;
        // 🚨 ENHANCED DEBUG: Erweiterte Logs für Minimum Order Validation
        console.log(`🛒🛒🛒 [${resolvedProjectId}] isMinimumOrderReached ENHANCED DEBUG:`, {
            timestamp: new Date().toISOString(),
            computedTrigger: 'isMinimumOrderReached_called',
            '--- BASIC STATE ---': true,
            hasInfo: !!info,
            hasPricingData: !!pricingData,
            isLoading,
            '--- PRICING DATA ANALYSIS ---': true,
            pricingStatus: pricingData?.status,
            pricingCode: pricingData?.code,
            pricingMessage: pricingData?.message,
            pricingDataKeys: pricingData ? Object.keys(pricingData) : [],
            hasServerData: !!data,
            serverDataKeys: data ? Object.keys(data) : [],
            '--- DELIVERY TYPE ANALYSIS ---': true,
            serverDeliveryType: data?.delivery?.type,
            computedDeliveryType: deliveryType,
            isPickup,
            deliveryFeeCent: data?.delivery?.feeCent,
            deliveryFeeEuro: deliveryFee,
            legacyDeliveryFee: data?.pricing?.deliveryFee,
            '--- MINIMUM ORDER INFO ---': true,
            minimumOrderInfo: info,
            infoIsReached: info?.isReached,
            infoRequired: info?.required,
            infoCurrent: info?.current,
            infoMissing: info?.missing,
            infoMessage: info?.message,
            '--- SERVER VALIDATION ---': true,
            hasServerValidation: !!data?.validation,
            serverMinimumOrder: data?.validation?.minimumOrder,
            serverValidationKeys: data?.validation ? Object.keys(data.validation) : [],
            '--- ERROR ANALYSIS ---': true,
            isMinimumOrderError: pricingData?.status === 'error' && pricingData?.code === 'MINIMUM_ORDER_NOT_REACHED',
            errorData: pricingData?.status === 'error' ? pricingData.data : null,
            '--- COMPUTED VALUES ---': true,
            currentSubtotal: subtotal.value,
            totalAmount: total.value
        });
        // CRITICAL: If still loading, don't allow submission yet
        if (isLoading) {
            console.log(`🛒⏳ [${resolvedProjectId}] Still loading - minimum order: false (safety)`, {
                reason: 'pricing_calculation_in_progress',
                safetyMeasure: true
            });
            return false;
        }
        // ✅ CRITICAL: For pickup orders, use server validation but be more lenient
        if (isPickup) {
            // First check if we have explicit minimum order info from server
            if (info !== null && info !== undefined) {
                const result = info.isReached;
                console.log(`🛒🚚📊 [${resolvedProjectId}] PICKUP: Using server minimum order validation: ${result}`, {
                    validationSource: 'server_minimum_order_info_for_pickup',
                    required: info.required,
                    current: info.current,
                    missing: info.missing,
                    isReached: info.isReached,
                    message: info.message,
                    deliveryFee,
                    subtotal: subtotal.value
                });
                return result;
            }
            // For pickup orders without explicit validation, only block if server explicitly says no
            if (pricingData?.status === 'error' && pricingData?.code === 'MINIMUM_ORDER_NOT_REACHED') {
                console.log(`🛒🚚❌ [${resolvedProjectId}] PICKUP: Server explicitly blocks minimum order: false`, {
                    serverError: pricingData.code,
                    serverMessage: pricingData.message,
                    serverData: pricingData.data,
                    reason: 'server_explicit_minimum_order_error_for_pickup'
                });
                return false;
            }
            // Otherwise allow pickup orders (they usually have lower or no minimum order)
            console.log(`🛒🚚✅ [${resolvedProjectId}] PICKUP: Allowing order (pickup usually no minimum): true`, {
                deliveryType,
                deliveryFee,
                subtotal: subtotal.value,
                reason: 'pickup_orders_typically_no_minimum_requirement'
            });
            return true;
        }
        // ✅ For delivery orders, enforce minimum order validation
        if (info !== null && info !== undefined) {
            const result = info.isReached;
            console.log(`🛒🚛 [${resolvedProjectId}] DELIVERY: Using explicit minimum order info: ${result}`, {
                validationSource: 'minimumOrderInfo_computed',
                required: info.required,
                current: info.current,
                missing: info.missing,
                isReached: info.isReached,
                message: info.message,
                deliveryFee,
                subtotal: subtotal.value
            });
            return result;
        }
        // If we have a MINIMUM_ORDER_NOT_REACHED error for delivery, definitely not reached
        if (pricingData?.status === 'error' && pricingData?.code === 'MINIMUM_ORDER_NOT_REACHED') {
            console.log(`🛒🚛❌ [${resolvedProjectId}] DELIVERY: MINIMUM_ORDER_NOT_REACHED error: false`, {
                serverError: pricingData.code,
                serverMessage: pricingData.message,
                serverData: pricingData.data,
                deliveryType,
                reason: 'server_explicit_minimum_order_not_reached_error'
            });
            return false;
        }
        // If we have successful pricing data but no minimum order info, check delivery fee
        if (pricingData?.status === 'success' && pricingData?.data) {
            // Legacy check removed - deliveryFee property no longer exists in new API
            // const legacyDeliveryFee = pricingData.data.pricing?.deliveryFee || 0
            // For pickup orders (no delivery fee), usually no minimum order requirement
            if (deliveryFee === 0) {
                console.log(`🛒🚚✅ [${resolvedProjectId}] Pickup order (no delivery fee) - no minimum order: true`, {
                    newDeliveryFee: deliveryFee,
                    subtotal: subtotal.value,
                    reason: 'no_delivery_fee_indicates_pickup'
                });
                return true;
            }
            // For delivery with success but no minimum order violation, assume requirement met
            console.log(`🛒🚛✅ [${resolvedProjectId}] Delivery with success - minimum order requirement met: true`, {
                pricingStatus: pricingData.status,
                deliveryFee,
                subtotal: subtotal.value,
                reason: 'successful_pricing_with_delivery_fee_assumes_minimum_met'
            });
            return true;
        }
        // Default: if no pricing data yet or error, don't allow submission for safety
        console.log(`🛒❌ [${resolvedProjectId}] No clear pricing state - minimum order: false (safety default)`, {
            hasPricingData: !!pricingData,
            pricingStatus: pricingData?.status,
            pricingCode: pricingData?.code,
            hasInfo: !!info,
            isLoading,
            reason: 'safety_default_when_no_clear_pricing_state'
        });
        return false;
    });
    const minimumOrderMessage = computed(() => {
        const info = minimumOrderInfo.value;
        if (!info || info.isReached)
            return null;
        return info.message || `Mindestbestellwert Warenwert: ${info.required.toFixed(2)}€ (noch ${info.missing.toFixed(2)}€ fehlen)`;
    });
    // Overall order status message for UI display
    const orderStatusMessage = computed(() => {
        const pricingData = projectPricingState.pricingData;
        const data = pricingData?.data;
        const deliveryType = data?.delivery?.type || 'pickup';
        console.log(`💬💬 [${resolvedProjectId}] orderStatusMessage ENHANCED DEBUG:`, {
            loading: projectPricingState.loading,
            hasPricingData: !!pricingData,
            status: pricingData?.status,
            code: pricingData?.code,
            deliveryType,
            hasData: !!pricingData?.data,
            minimumOrderValidation: data?.validation?.minimumOrder
        });
        if (projectPricingState.loading) {
            return 'Berechne Preise...';
        }
        if (!pricingData) {
            console.log(`💬❌ [${resolvedProjectId}] No pricing data available for status message`);
            return null;
        }
        // Restaurant closed - can now be status: 'success' with code: 'RESTAURANT_CLOSED'
        if (pricingData.code === 'RESTAURANT_CLOSED') {
            const message = pricingData.message || 'Restaurant ist geschlossen';
            console.log(`💬🚫 [${resolvedProjectId}] Restaurant closed message: ${message}`);
            return message;
        }
        // Minimum order not reached - ENHANCED with delivery type awareness
        if (pricingData.status === 'error' && pricingData.code === 'MINIMUM_ORDER_NOT_REACHED') {
            const errorData = pricingData.data;
            if (errorData) {
                const orderTypeText = deliveryType === 'delivery' ? 'Lieferung' : 'Abholung';
                const message = `Mindestbestellwert für ${orderTypeText}: ${errorData.minimumRequired}€ (noch ${errorData.missingAmount}€ fehlen)`;
                console.log(`💬📊 [${resolvedProjectId}] Enhanced minimum order message: ${message}`, {
                    errorData,
                    deliveryType,
                    orderTypeText
                });
                return message;
            }
            const fallbackMessage = pricingData.message || 'Mindestbestellwert nicht erreicht';
            console.log(`💬📊 [${resolvedProjectId}] Minimum order fallback message: ${fallbackMessage}`);
            return fallbackMessage;
        }
        // Success - but check if we should show any informational messages
        if (pricingData.status === 'success') {
            // Check if there are any validation messages even for successful orders
            const minimumOrderValidation = data?.validation?.minimumOrder;
            if (minimumOrderValidation && !minimumOrderValidation.isReached) {
                const orderTypeText = deliveryType === 'delivery' ? 'Lieferung' : 'Abholung';
                const message = `Mindestbestellwert für ${orderTypeText}: ${minimumOrderValidation.required}€ (noch ${minimumOrderValidation.missing}€ fehlen)`;
                console.log(`💬⚠️ [${resolvedProjectId}] Success but minimum order not reached: ${message}`, {
                    minimumOrderValidation,
                    deliveryType
                });
                return message;
            }
            console.log(`💬✅ [${resolvedProjectId}] Success status - no message needed`);
            return null;
        }
        // Other errors
        const errorMessage = pricingData.message || 'Unbekannter Fehler';
        console.log(`💬❓ [${resolvedProjectId}] Other error message: ${errorMessage}`, { pricingData });
        return errorMessage;
    });
    // ✅ OPTIMIZED: Generate breakdown data client-side using MoneyUtils for consistency
    // Server no longer sends formatted breakdown - we calculate it for better performance
    const breakdown = computed(() => {
        const pricingData = projectPricingState.pricingData;
        if (pricingData?.status === 'success' && pricingData.data) {
            // Generate client-side breakdown using MoneyUtils for consistent formatting
            return {
                subtotal: formatCurrency(subtotal.value),
                deliveryFee: formatCurrency(deliveryFee.value),
                serviceFee: formatCurrency(serviceFee.value),
                tax: formatCurrency(tax.value),
                total: formatCurrency(total.value)
            };
        }
        return {};
    });
    // Formatted values
    const formattedSubtotal = computed(() => formatCurrency(subtotal.value));
    const formattedTax = computed(() => formatCurrency(tax.value));
    const formattedServiceFee = computed(() => formatCurrency(serviceFee.value));
    const formattedDeliveryFee = computed(() => formatCurrency(deliveryFee.value));
    const formattedTotal = computed(() => formatCurrency(total.value));
    const composableInstance = {
        // State (project-specific)
        pricingData: computed(() => projectPricingState.pricingData),
        loading: isLoading,
        error,
        lastCalculatedAt: computed(() => projectPricingState.lastCalculatedAt),
        requestStatus: computed(() => projectPricingState.requestStatus),
        // Methods (project-aware)
        calculateOrderPricing,
        clearPricing,
        clearDiscountCode, // ✅ NEW: Clear stored discount code
        formatCurrency,
        formatPrice: formatCurrency, // Alias for backward compatibility
        autoInitialize,
        // Computed Values (project-specific)
        subtotal,
        tax,
        serviceFee,
        deliveryFee,
        total,
        totalAmount, // Alias for total
        currency,
        taxBreakdown,
        taxCalculation,
        breakdown,
        // Formatted Values (project-specific)
        formattedSubtotal,
        formattedTax,
        formattedServiceFee,
        formattedDeliveryFee,
        formattedTotal,
        // Utils (project-specific)
        hasPricing,
        isLoading,
        hasError,
        // Restaurant Status
        isRestaurantClosed,
        restaurantClosedMessage,
        // Minimum Order Status
        minimumOrderInfo,
        isMinimumOrderReached,
        minimumOrderMessage,
        orderStatusMessage,
        // ✅ NEU: Lieferzonenvalidierung ohne vollständige Preisberechnung
        checkDeliveryZone: async (deliveryAddress) => {
            console.log(`🚀 [${resolvedProjectId}:${instanceId}] checkDeliveryZone START`, { deliveryAddress });
            try {
                const result = await apiClient.checkDeliveryZone(deliveryAddress);
                console.log(`✅ [${resolvedProjectId}:${instanceId}] checkDeliveryZone result:`, result);
                return result;
            }
            catch (error) {
                console.error(`❌ [${resolvedProjectId}:${instanceId}] checkDeliveryZone error:`, error);
                return {
                    success: false,
                    inZone: false,
                    error: error instanceof Error ? error.message : 'Unbekannter Fehler'
                };
            }
        },
        // ✅ NEW: Calculate single product price (für ProductConfigModal)
        calculateProductPrice: async (productId, quantity, options = {}) => {
            console.log(`🚀 [${resolvedProjectId}:${instanceId}] calculateProductPrice START`, {
                productId,
                quantity,
                options
            });
            try {
                const result = await apiClient.calculateProductPrice({
                    productId,
                    quantity,
                    selectedSizeId: options.selectedSizeId,
                    extraIngredients: options.extraIngredients,
                    removedIngredients: options.removedIngredients
                });
                console.log(`✅ [${resolvedProjectId}:${instanceId}] calculateProductPrice result:`, result);
                return result;
            }
            catch (error) {
                console.error(`❌ [${resolvedProjectId}:${instanceId}] calculateProductPrice error:`, error);
                throw error;
            }
        },
        // ✅ NEU: Erweiterte Preisberechnung mit Lieferadresse
        calculateOrderPricingWithAddress: async (items, deliveryType = 'pickup', deliveryAddress) => {
            return calculateOrderPricing(items, deliveryType, deliveryAddress);
        },
        // Debug info
        _state: projectPricingState,
        _projectId: resolvedProjectId,
        _instanceId: instanceId
    };
    // SINGLETON: Store the instance for reuse
    projectComposableInstances.set(resolvedProjectId, composableInstance);
    console.log(`💾 [${resolvedProjectId}] Stored SINGLETON instance:`, instanceId);
    // Expose debug functions globally (only in development)
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        window.debugPricing = {
            getActiveInstances,
            debugActiveInstances,
            getAllProjectStates,
            clearProjectState,
            projectComposableInstances,
            projectPricingStates
        };
    }
    return composableInstance;
}
